import { useState, useEffect, useRef } from "react";
import { useTour } from "./useTour";

function resolve(v, ...args) {
  return typeof v === "function" ? v(...args) : v;
}

/* The overlay: dims the page, cuts a brass-ringed hole around the current
   target (box-shadow trick), and floats a themed tooltip. Each step may first
   run `enter(controls, isMobile)` to set up the screen (open a menu/drawer,
   switch tab); we then measure across a few frames + delays so the target is
   found after any open animation. A hidden/zero-size or missing target falls
   back to a centered card. Cleanup runs `leave(...)` to undo the setup. */
export default function Spotlight() {
  const {
    step,
    stepIndex,
    steps,
    isFirst,
    isLast,
    isMobile,
    controls,
    next,
    prev,
    finish,
    skip,
  } = useTour();
  const [rect, setRect] = useState(null);
  const nextRef = useRef(null);

  useEffect(() => {
    const sel = resolve(step.target, isMobile);
    const ctrls = controls.current;
    step.enter?.(ctrls, isMobile);

    // Scroll the target into view the first time it's actually found (it may
    // only appear after a tab switch / route change / fetch), and only if it's
    // off-screen — so an already-visible target doesn't jump.
    let scrolled = false;
    const measure = () => {
      const el = sel ? document.querySelector(sel) : null;
      if (el) {
        const r0 = el.getBoundingClientRect();
        if (r0.width > 0 && r0.height > 0) {
          if (!scrolled) {
            scrolled = true;
            const inView = r0.top >= 0 && r0.bottom <= window.innerHeight;
            if (!inView) el.scrollIntoView({ block: "center", inline: "nearest" });
          }
          const r = el.getBoundingClientRect();
          setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
          return;
        }
      }
      setRect(null); // missing / hidden target → centered fallback
    };

    // Measure after a paint, then again past drawer/menu open animations and
    // async-loaded content (e.g. the privacy toggles or Review shoes after a fetch).
    const raf = requestAnimationFrame(() => requestAnimationFrame(measure));
    const timers = [170, 380, 700, 1200].map((d) => setTimeout(measure, d));
    window.addEventListener("resize", measure);
    window.addEventListener("scroll", measure, true);

    return () => {
      cancelAnimationFrame(raf);
      timers.forEach(clearTimeout);
      window.removeEventListener("resize", measure);
      window.removeEventListener("scroll", measure, true);
      step.leave?.(ctrls, isMobile);
    };
  }, [step, controls, isMobile]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && skip();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [skip]);
  useEffect(() => {
    const t = setTimeout(() => nextRef.current?.focus(), 60);
    return () => clearTimeout(t);
  }, [stepIndex]);

  // Focus trap: keep Tab focus inside the tooltip (never onto the dimmed page).
  const tipRef = useRef(null);
  useEffect(() => {
    const onKey = (e) => {
      if (e.key !== "Tab") return;
      const f = tipRef.current?.querySelectorAll("button:not([disabled])");
      if (!f || !f.length) return;
      const first = f[0];
      const last = f[f.length - 1];
      if (e.shiftKey && document.activeElement === first) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && document.activeElement === last) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, []);

  const hasHole = !!rect;
  const PAD = 8;
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const TIP_W = Math.min(step.gate ? 460 : 340, vw - 24);

  let tip;
  if (hasHole) {
    const placeBelow = rect.top + rect.height + 180 < vh;
    let left = rect.left + rect.width / 2 - TIP_W / 2;
    left = Math.max(12, Math.min(left, vw - TIP_W - 12));
    tip = placeBelow
      ? { left, top: rect.top + rect.height + 14 }
      : { left, bottom: vh - rect.top + 14 };
  } else {
    tip = { left: vw / 2 - TIP_W / 2, top: Math.max(24, vh / 2 - 110) };
  }

  const holeBox = hasHole
    ? {
        top: rect.top - PAD,
        left: rect.left - PAD,
        width: rect.width + PAD * 2,
        height: rect.height + PAD * 2,
      }
    : null;

  return (
    <div
      className="tour-root"
      role="dialog"
      aria-modal="true"
      aria-labelledby="tour-title"
      aria-describedby="tour-body"
    >
      <div className={`tour-backdrop ${hasHole ? "" : "dim"}`} />
      {hasHole && <div className="tour-hole" style={holeBox} />}

      <div className="tour-tip" ref={tipRef} style={{ width: TIP_W, ...tip }}>
        <div className="tour-tip-title" id="tour-title">
          {step.title}
        </div>
        <p className="tour-tip-body" id="tour-body">
          {resolve(step.body, isMobile)}
        </p>
        <div className="tour-tip-foot">
          {step.gate ? (
            <span className="tour-nav tour-gate-nav">
              <button type="button" className="btn small" onClick={skip}>
                I&rsquo;m good, let me play
              </button>
              <button
                type="button"
                ref={nextRef}
                className="btn small primary"
                onClick={next}
              >
                Show me the rest
              </button>
            </span>
          ) : (
            <>
              <button type="button" className="tour-skip" onClick={skip}>
                Skip tour
              </button>
              <span className="tour-count" aria-hidden="true">
                {stepIndex + 1} / {steps.length}
              </span>
              <span className="tour-nav">
                {!isFirst && (
                  <button type="button" className="btn small" onClick={prev}>
                    Back
                  </button>
                )}
                <button
                  type="button"
                  ref={nextRef}
                  className="btn small primary"
                  onClick={isLast ? finish : next}
                >
                  {isLast ? "Done" : "Next"}
                </button>
              </span>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
